import { Check, Columns3 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';

export type ColumnKey =
  | 'date'
  | 'member'
  | 'title'
  | 'description'
  | 'project'
  | 'duration'
  | 'hourlyRate'
  | 'totalAmount'
  | 'billingStatus';

export const ALL_COLUMNS: ColumnKey[] = [
  'date',
  'member',
  'title',
  'description',
  'project',
  'duration',
  'hourlyRate',
  'totalAmount',
  'billingStatus',
];

export const DEFAULT_VISIBLE_COLUMNS: ColumnKey[] = [
  'date',
  'member',
  'title',
  'project',
  'duration',
  'totalAmount',
  'billingStatus',
];

const COLUMN_LABELS: Record<ColumnKey, string> = {
  date: 'Date',
  member: 'Member',
  title: 'Title',
  description: 'Description',
  project: 'Project',
  duration: 'Duration',
  hourlyRate: 'Hourly Rate',
  totalAmount: 'Value',
  billingStatus: 'Billing Status',
};

interface ColumnSelectorProps {
  value: ColumnKey[];
  onChange: (columns: ColumnKey[]) => void;
}

export function ColumnSelector({ value, onChange }: ColumnSelectorProps) {
  const toggleColumn = (column: ColumnKey) => {
    if (value.includes(column)) {
      onChange(value.filter((c) => c !== column));
    } else {
      onChange([...value, column]);
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm">
          <Columns3 className="mr-2 h-4 w-4" />
          Columns
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-3" align="end">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-sm font-medium">Visible columns</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-auto px-2 py-1 text-xs"
            onClick={() => onChange([...ALL_COLUMNS])}
          >
            Select all
          </Button>
        </div>
        <div className="space-y-1">
          {ALL_COLUMNS.map((column) => {
            const isSelected = value.includes(column);
            return (
              <button
                key={column}
                type="button"
                onClick={() => toggleColumn(column)}
                className={`flex w-full items-center justify-between rounded-md px-2 py-1.5 text-sm transition-colors ${
                  isSelected
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-muted'
                }`}
              >
                {COLUMN_LABELS[column]}
                {isSelected && <Check className="h-4 w-4" />}
              </button>
            );
          })}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="mt-2 h-auto w-full px-2 py-1 text-xs"
          onClick={() => onChange([...DEFAULT_VISIBLE_COLUMNS])}
        >
          Reset to default
        </Button>
      </PopoverContent>
    </Popover>
  );
}
